import React, { useState } from 'react';
import { Center, useBreakpointValue, Heading, Box, Button } from '@companydotcom/potion';
import { useForm, useFieldArray } from 'react-hook-form';
import { isEmpty, uniq } from 'lodash';
import { PageDivider } from '@companydotcom/ui';
import { useTranslation } from 'react-i18next';
import {
  useGetAcgQueryArgs,
  useGetAcgReferenceDataV2Query,
  useGetAcgUserProfileV2Query,
  useUpdateAcgUserV2Mutation,
} from '../../../../services/acg/acg-api-v2';
import { useGetGlobalUserQuery } from '../../../../services/user/user-api';
import { AdditiveList } from '../../../../components/elements';

export interface EditIndustryProps {
  onClose: () => void;
  isMobile: boolean;
}

export const EditIndustry = ({ onClose, isMobile }: EditIndustryProps) => {
  const { t } = useTranslation();
  const { data: globalUser } = useGetGlobalUserQuery();
  const args = useGetAcgQueryArgs();
  const updateUserArgs = useGetAcgQueryArgs(['email', 'source']);
  const { data: acgUserProfileData } = useGetAcgUserProfileV2Query({
    ...args,
    email: globalUser?.email,
    accountId: globalUser?.accountId,
  });
  const { data: acgRefData, isLoading } = useGetAcgReferenceDataV2Query({
    ...args,
    referenceData: ['industry'],
  });

  const defaultValues = {
    industry: acgUserProfileData?.industry?.map(i => ({ name: i })) ?? [],
  };
  const headingSize = useBreakpointValue({ base: 'hs-md', md: 'hs-2xl' });
  const [showOptions, setShowOptions] = useState(isEmpty(defaultValues.industry));
  const [updateAcgUser, { isLoading: isUpdating }] = useUpdateAcgUserV2Mutation();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
  } = useForm({
    defaultValues,
  });
  const { fields, append, remove } = useFieldArray({ name: 'industry', control });

  const addData = (e: React.ChangeEvent<HTMLSelectElement>) => {
    append({
      name: e.target?.options?.[e.target.selectedIndex]?.text,
    });
    setShowOptions(false);
  };

  const removeData = (index: number) => {
    if (fields?.length === 1) {
      setShowOptions(true);
    }

    remove(index);
  };

  const onSubmit = async (data: typeof defaultValues) => {
    try {
      if (globalUser) {
        const payload = {
          lastProfileUpdateVersion: acgUserProfileData?.lastProfileUpdateVersion ?? 1,
          industry: data?.industry.map(i => i.name),
        };

        updateAcgUser({
          ...updateUserArgs,
          payload,
        });
        onClose();
      }
    } catch (error) {
      console.log('EditIndustry onSubmit error: ', error);
    }
  };

  return (
    <Center flexDirection="column">
      <Heading size={headingSize} fontWeight="700" mt={[6, 12]} mb={[2, 2, 12]}>
        {t('acg.myProfile.profilePages.industries.industry')}
      </Heading>
      {isMobile && <PageDivider width={50} mb={12} />}
      <Box maxWidth="460px" width="100%">
        <AdditiveList
          removeHandler={removeData}
          addHandler={addData}
          fields={fields}
          showOptions={showOptions}
          setShowOptions={setShowOptions}
          label="Add industry"
          isLoading={isUpdating || isLoading}
        >
          {acgRefData?.industry
            ? uniq(acgRefData.industry).map(opt => (
                <option
                  key={opt?.recordName!}
                  value={opt?.recordName!}
                  disabled={fields?.some(field => field?.name === opt?.recordName!)}
                >
                  {opt?.recordName!}
                </option>
              ))
            : []}
        </AdditiveList>
      </Box>
      <Box my={12}>
        <Button
          variant="outline"
          mr="2"
          onClick={onClose}
          size="sm"
          isDisabled={isUpdating || isSubmitting}
        >
          {t('common.buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          type="submit"
          size="sm"
          // isLoading={isSubmitting}
          isDisabled={!isValid || !isDirty}
        >
          {t('common.buttons.save')}
        </Button>
      </Box>
    </Center>
  );
};
